<template>
  <div class="sp">
    <div class="sp-head">
      <div class="sp-label">Sending overview</div>
      <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="load">Refresh</button>
    </div>
    <div v-if="loading" class="text-muted small">Loading...</div>
    <template v-else-if="data">
      <table class="sp-table">
        <tbody>
          <tr><th>State</th><td>{{ data.state }}{{ data.reason ? ' (' + data.reason + ')' : '' }}</td></tr>
          <tr><th>Cold email</th><td>{{ data.cold_policy === 'any' ? 'any company' : 'registered companies and public sector only' }}</td></tr>
          <tr>
            <th>Daily cap</th>
            <td>
              {{ data.caps.used_today }} of {{ data.caps.cap }} used today
              ({{ data.caps.own_cap === null ? 'platform default' : 'its own cap' }}{{ data.caps.enforced ? '' : ', caps switched off' }})
            </td>
          </tr>
          <tr><th>Held</th><td>{{ data.held.total }}</td></tr>
          <tr>
            <th>Deferred</th>
            <td>daily cap {{ data.deferred.daily_cap }}, warm-up {{ data.deferred.warmup }}, volume guard {{ data.deferred.volume_guard }}</td>
          </tr>
          <tr>
            <th>Last {{ data.health.window_days }} days</th>
            <td>
              {{ data.health.sent }} outreach sent, {{ data.health.bounced }} bounced ({{ pct(data.health.bounce_rate) }}, pause at {{ pct(data.health.thresholds.bounce_rate) }}),
              {{ data.health.complained }} complaints ({{ pct(data.health.complaint_rate) }}, pause at {{ pct(data.health.thresholds.complaint_rate) }}).
              <span v-if="data.health.over_limits.length" class="sp-bad">Over the limit: {{ data.health.over_limits.join('; ') }}.</span>
              <span v-else-if="data.health.sent < data.health.thresholds.min_sent" class="sp-muted">The rates count once {{ data.health.thresholds.min_sent }} have been sent.</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-for="d in data.domains" :key="d.id" class="sp-domain">
        <strong>{{ d.domain }}</strong>
        <span class="sp-muted"> - sending records {{ d.state }};
          warm-up {{ d.warmup_enabled ? (d.warmup_day ? 'day ' + d.warmup_day + ', ' + d.used_today + ' of ' + d.today_cap + ' today' : 'not started') : 'off' }};
          replies {{ d.ready ? 'ready' : 'not ready (' + d.reasons.join('; ') + ')' }}{{ d.used_by_senders ? '' : '; not used by its senders' }}</span>
      </div>
      <p v-if="!data.domains.length" class="sp-muted">No sending domains.</p>

      <div class="sp-head sp-head--sub">
        <div class="sp-label">Pre-flight</div>
        <button type="button" class="btn btn-sm btn-outline-primary" :disabled="checking" @click="runPreflight">
          {{ checking ? 'Checking...' : 'Run pre-flight' }}
        </button>
      </div>
      <p class="sp-muted">
        The checklist for switching this workspace's sending on, the same as daedalus sending:preflight. It reads only: it looks up the
        domain's DNS records and changes nothing.
      </p>
      <template v-if="preflight">
        <p :class="preflight.ready ? 'sp-good' : 'sp-bad'">
          {{ preflight.counts.pass }} pass, {{ preflight.counts.fail }} fail, {{ preflight.counts.manual }} to check by hand.
        </p>
        <div v-for="i in preflight.items" :key="i.id" class="sp-item">
          <span :class="'sp-status sp-status--' + i.status">{{ i.status }}</span>
          <span class="sp-item__id">{{ i.id }}</span>
          <span>{{ i.title }}</span>
          <div v-for="(line, n) in i.detail" :key="n" class="sp-item__detail">{{ line }}</div>
        </div>
      </template>
    </template>
    <div v-if="error" class="alert alert-danger py-1 px-2 small mb-0">{{ error }}</div>
  </div>
</template>

<script>
/**
 * SendingPanel - the operator's read-only overview of one workspace's
 * sending (send safety slice 12), in the tenant drawer below the sending
 * controls: state, cold policy, daily cap and today's use, held and
 * deferred counts, the last 7 days against the automatic pause's limits,
 * each sending domain's warm-up and reply readiness, and the pre-flight
 * checklist on request. Reads /api/v1/admin/sending/workspaces/{id}/panel and
 * /preflight (Ops::PLATFORM).
 */
import useApi from "@apexl/icarus-core/src/services/api";

const BASE = '/api/v1/admin/sending/workspaces';

export default {
  name: 'SendingPanel',
  props: {
    tenantId: { type: Number, required: true },
  },
  data() {
    return { api: useApi(), loading: true, checking: false, data: null, preflight: null, error: '' };
  },
  watch: {
    tenantId() { this.preflight = null; this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true; this.error = '';
      try {
        const p = await this.api.get(`${BASE}/${this.tenantId}/panel`);
        this.data = p?.data || null;
        if (p?.error) this.error = p.error;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not load the sending overview.';
      }
      this.loading = false;
    },
    async runPreflight() {
      this.checking = true; this.error = '';
      try {
        const p = await this.api.get(`${BASE}/${this.tenantId}/preflight`);
        this.preflight = p?.data || null;
        if (p?.error) this.error = p.error;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Could not run the pre-flight.';
      }
      this.checking = false;
    },
    pct(f) {
      const p = Number(f || 0) * 100;
      return `${Number(p.toFixed(p < 1 ? 2 : 1))}%`;
    },
  },
};
</script>

<style scoped>
.sp-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.sp-head--sub { margin-top: 14px; }
.sp-label { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); }
.sp-table { width: 100%; font-size: 0.8rem; margin-bottom: 8px; }
.sp-table th { font-weight: 600; color: var(--m-text-secondary); padding: 2px 10px 2px 0; vertical-align: top; white-space: nowrap; }
.sp-table td { padding: 2px 0; }
.sp-domain { font-size: 0.8rem; border-top: 1px solid var(--m-border, #eaecf3); padding: 6px 0; }
.sp-muted { font-size: 0.78rem; color: var(--m-text-tertiary); }
.sp-good { font-size: 0.8rem; color: var(--m-success, #067647); margin: 0 0 6px; }
.sp-bad { font-size: 0.8rem; color: var(--m-danger, #b42318); margin: 0 0 6px; }
.sp-item { font-size: 0.78rem; padding: 3px 0; border-top: 1px dashed var(--m-border, #eaecf3); }
.sp-item__id { display: inline-block; min-width: 30px; font-weight: 700; }
.sp-item__detail { color: var(--m-text-tertiary); padding-left: 88px; }
.sp-status { display: inline-block; min-width: 54px; font-weight: 700; text-transform: uppercase; font-size: 0.66rem; }
.sp-status--pass { color: var(--m-success, #067647); }
.sp-status--fail { color: var(--m-danger, #b42318); }
.sp-status--manual { color: var(--m-text-secondary); }
</style>
